import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom'
import { getClassStudents } from "../../redux/sclassRelated/sclassHandle";
import { 
    Paper, Box, Typography, ButtonGroup, Button, Popper, Grow, 
    ClickAwayListener, MenuList, MenuItem, Alert, Grid, Card, 
    CardContent, Container, Fade, Avatar, Divider, Tooltip, Chip,
    IconButton, CircularProgress
} from '@mui/material';
import { 
    KeyboardArrowDown, 
    KeyboardArrowUp,
    Group as GroupIcon,
    Class as ClassIcon,
    Subject as SubjectIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Assessment as AssessmentIcon,
    ArrowBack as ArrowBackIcon
} from "@mui/icons-material";
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import TableTemplate from "../../components/TableTemplate";

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 80px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const PremiumCard = styled(Paper)`
    border-radius: 20px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    box-shadow: 0 15px 35px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    margin-bottom: 30px;
`;

const SelectionCard = styled(Card)`
    border-radius: 16px !important;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    border: 2px solid ${props => props.$active ? '#6366f1' : 'transparent'} !important;
    background: ${props => props.$active ? '#f5f7ff' : 'white'} !important;
    
    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 12px 24px -10px rgba(0, 0, 0, 0.1) !important;
        border-color: ${props => props.$active ? '#6366f1' : '#e2e8f0'} !important;
    }
`;

const TeacherClassDetails = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const params = useParams();
    const classID = params.id;

    const { sclassStudents, loading, error, getresponse } = useSelector((state) => state.sclass);
    const { currentUser } = useSelector((state) => state.user);
    
    const [teacherClasses, setTeacherClasses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState(classID || '');
    const [classSubjects, setClassSubjects] = useState([]);
    const [message, setMessage] = useState({ type: '', text: '' });

    const teacherId = currentUser?._id;

    useEffect(() => {
        if (teacherId) {
            fetchTeacherClasses();
        }
    }, [teacherId]);

    useEffect(() => {
        if (selectedClassId) {
            dispatch(getClassStudents(selectedClassId));
            fetchClassSubjects();
        }
    }, [dispatch, selectedClassId]);

    const fetchTeacherClasses = async () => {
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const result = await axios.get(`${apiUrl}/Teacher/Classes/${teacherId}`);
            if (Array.isArray(result.data) && result.data.length > 0) {
                setTeacherClasses(result.data);
                if (!selectedClassId && !classID) {
                    setSelectedClassId(result.data[0]._id);
                }
            }
        } catch (error) {
            console.error('Error fetching teacher classes:', error);
        }
    };

    const fetchClassSubjects = async () => {
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const result = await axios.get(`${apiUrl}/Teacher/Subjects/${teacherId}/${selectedClassId}`);
            if (Array.isArray(result.data)) {
                setClassSubjects(result.data);
            }
        } catch (error) {
            console.error('Error fetching subjects:', error);
        }
    };

    const handleClassChange = (newClassId) => {
        setSelectedClassId(newClassId);
        navigate(`/Teacher/class/${newClassId}`);
    };

    const selectedClass = teacherClasses.find(c => c._id === selectedClassId);

    const studentColumns = [
        { id: 'photo', label: 'Photo', minWidth: 80 },
        { id: 'name', label: 'Name', minWidth: 170 },
        { id: 'rollNum', label: 'Roll Number', minWidth: 100 },
    ]

    const studentRows = sclassStudents && sclassStudents.map((student) => {
        return {
            photo: student.photo,
            name: student.name,
            rollNum: student.rollNum,
            id: student._id,
        };
    })

    const StudentsButtonHaver = ({ row }) => {
        const options = ['Take Attendance', 'Provide Marks'];
        const [open, setOpen] = React.useState(false);
        const anchorRef = React.useRef(null);
        const [selectedIndex, setSelectedIndex] = React.useState(0);

        const handleClick = () => {
            if (selectedIndex === 0) navigate(`/Teacher/attendance?classId=${selectedClassId}`);
            else if (selectedIndex === 1) navigate(`/Teacher/marks?classId=${selectedClassId}`);
        };

        return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Tooltip title="View Profile">
                    <IconButton 
                        onClick={() => navigate("/Teacher/class/student/" + row.id)}
                        sx={{ color: '#6366f1', bgcolor: '#eef2ff', '&:hover': { bgcolor: '#e0e7ff' } }}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                
                <ButtonGroup variant="contained" ref={anchorRef} size="small" sx={{ borderRadius: '8px', overflow: 'hidden', boxShadow: 'none' }}>
                    <Button 
                        onClick={handleClick} 
                        sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#6366f1' }}
                    >
                        {options[selectedIndex]}
                    </Button>
                    <Button
                        size="small"
                        onClick={() => setOpen((prev) => !prev)}
                        sx={{ bgcolor: '#4f46e5', minWidth: '32px' }}
                    >
                        {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                    </Button>
                </ButtonGroup>
                
                <Popper open={open} anchorEl={anchorRef.current} transition disablePortal sx={{ zIndex: 10 }}>
                    {({ TransitionProps, placement }) => (
                        <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
                            <Paper sx={{ mt: 1, borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' }}>
                                <ClickAwayListener onClickAway={() => setOpen(false)}>
                                    <MenuList>
                                        {options.map((option, index) => (
                                            <MenuItem
                                                key={option}
                                                selected={index === selectedIndex}
                                                onClick={() => { setSelectedIndex(index); setOpen(false); }}
                                                sx={{ fontSize: '0.875rem', fontWeight: 500, px: 2 }}
                                            >
                                                {index === 0 ? <CheckCircleIcon sx={{ mr: 1, fontSize: 18, color: '#10b981' }} /> : <AssessmentIcon sx={{ mr: 1, fontSize: 18, color: '#f59e0b' }} />}
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </Box>
        );
    };

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="lg">
                    <Fade in timeout={800}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <IconButton onClick={() => navigate('/Teacher/dashboard')} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                    <ArrowBackIcon />
                                </IconButton>
                                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                    Class Management
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400, ml: 8 }}>
                                {selectedClass ? `Managing Class ${selectedClass.sclassName}` : 'Select a class to view student details and manage academic tasks.'}
                            </Typography>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg" sx={{ mt: '5px' }}>
                {/* Class Selection Grid */}
                <Box sx={{ mb: 6 }}>
                    <Grid container spacing={3}>
                        {teacherClasses.map((cls) => (
                            <Grid item xs={12} sm={6} md={4} key={cls._id}>
                                <SelectionCard 
                                    $active={selectedClassId === cls._id}
                                    onClick={() => handleClassChange(cls._id)}
                                    elevation={selectedClassId === cls._id ? 8 : 1}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{ bgcolor: selectedClassId === cls._id ? '#6366f1' : '#f1f5f9', color: selectedClassId === cls._id ? 'white' : '#64748b', mr: 2 }}>
                                                <ClassIcon />
                                            </Avatar>
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                                Class {cls.sclassName}
                                            </Typography>
                                        </Box>
                                        
                                        <Divider sx={{ mb: 2 }} />
                                        
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {cls.subjects?.length > 0 ? (
                                                cls.subjects.map((sub, idx) => (
                                                    <Chip 
                                                        key={sub._id || idx} 
                                                        label={sub.subName} 
                                                        size="small" 
                                                        icon={<SubjectIcon sx={{ fontSize: '12px !important' }} />}
                                                        sx={{ fontSize: '0.75rem', fontWeight: 600, bgcolor: selectedClassId === cls._id ? '#eef2ff' : '#f8fafc' }} 
                                                    />
                                                ))
                                            ) : (
                                                <Typography variant="caption" color="textSecondary">No subjects assigned</Typography>
                                            )}
                                        </Box>
                                    </CardContent>
                                </SelectionCard>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Students List Section */}
                {selectedClassId && (
                    <Fade in timeout={1000}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 40, height: 40 }}>
                                        <GroupIcon />
                                    </Avatar>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                        Students Directory
                                    </Typography>
                                </Box>
                                <Chip 
                                    label={`${sclassStudents?.length || 0} Students`} 
                                    variant="outlined" 
                                    sx={{ fontWeight: 700, borderRadius: '8px' }} 
                                />
                            </Box>

                            {loading ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                                    <CircularProgress size={40} sx={{ mb: 2 }} />
                                    <Typography color="textSecondary" fontWeight="medium">Loading student list...</Typography>
                                </Box>
                            ) : getresponse ? (
                                <PremiumCard sx={{ p: 8, textAlign: 'center' }}>
                                    <GroupIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                                    <Typography variant="h5" sx={{ color: '#1e293b', fontWeight: 700 }}>
                                        No students found
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#64748b', mt: 1 }}>
                                        There are currently no students registered in this class.
                                    </Typography>
                                </PremiumCard>
                            ) : (
                                <PremiumCard sx={{ border: 'none !important', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06) !important' }}>
                                    <TableTemplate 
                                        buttonHaver={StudentsButtonHaver} 
                                        columns={studentColumns} 
                                        rows={studentRows} 
                                    />
                                </PremiumCard>
                            )}
                        </Box>
                    </Fade>
                )}
            </ContentContainer>
        </PageWrapper>
    );
};

export default TeacherClassDetails;
